import type {TextEditorSelection} from '../types/TextEditorApi';

const WORD_CHARACTER = /[\p{L}\p{M}\p{N}]/u;
const WORD_JOINER = /['’-]/;

/**
 * Widens a range that starts or ends inside a word to the whole word, so a rewrite of the range
 * never leaves half a word behind ("z" + "telefonický", "16:00" + "bychom"). A range that already
 * sits on word boundaries, or that includes the space or punctuation next to the word, is kept.
 * `charAt` returns `null` outside the document.
 */
export function expandTextEditorSelectionToWords(
  selection: TextEditorSelection,
  charAt: (index: number) => string | null
): TextEditorSelection {
  if (selection.length <= 0) {
    return selection;
  }

  const isWordCharacterAt = (index: number) => {
    const character = index < 0 ? null : charAt(index);

    return character !== null && character !== '' && WORD_CHARACTER.test(character);
  };
  const isWordPartAt = (index: number) => {
    if (isWordCharacterAt(index)) {
      return true;
    }
    const character = index < 0 ? null : charAt(index);

    // An apostrophe or a hyphen belongs to the word only between two letters ("Škoda's", "e-mail").
    return (
      character !== null &&
      WORD_JOINER.test(character) &&
      isWordCharacterAt(index - 1) &&
      isWordCharacterAt(index + 1)
    );
  };

  let start = selection.index;
  let end = selection.index + selection.length;

  if (isWordPartAt(start) && isWordPartAt(start - 1)) {
    while (isWordPartAt(start - 1)) {
      start = start - 1;
    }
  }
  if (isWordPartAt(end - 1) && isWordPartAt(end)) {
    while (isWordPartAt(end)) {
      end = end + 1;
    }
  }

  return start === selection.index && end === selection.index + selection.length
    ? selection
    : {index: start, length: end - start};
}
